/**
 * /tsg-collab (formerly /college-intro) — content model.
 *
 * THE QUESTION THIS PAGE ANSWERS: "What does our institution gain by
 * adopting TSG Nexus?" The student page (/student-intro) answers "What do I
 * get?" — same ecosystem, same product visuals, different story. Never copy a
 * student section across: reuse the asset, change the crop, callouts, copy
 * and benefit.
 *
 * SECTION ORDER (CollegeIntroPage.jsx composes them)
 *   1  hero         Turn your campus into an engineering experience.
 *   2  challenge    Why colleges need more than training (+ Academics + Nexus)
 *   3  needs        What colleges need → what TSG Nexus enables (+ proof)
 *   3b difference   From academic learning to industry experience (comparison,
 *                   dashboard + co-branded certificate story, college gains)
 *   4  journey      College → TSG Nexus → student → … → a job on ExiHire
 *   5  students     Your students get more than course content
 *   6  git          Help students build evidence of what they can do
 *   7  tracker      Turn academic projects into structured engineering experience
 *   8  mock         Help students practise before the real interview
 *   9  hire         Connecting student development with AI-powered hiring
 *  10  people       Faculty · Training & Placement · Management
 *  11  brand        Your college. Your brand. Your student experience.
 *  12  shift        From training programme to continuous industry readiness
 *  13  why          Why TSG Nexus for your college (feature → student → college)
 *  14  hub          One ecosystem. Multiple institutional advantages.
 *  15  start        Get started + partnership terms
 *  16  close        Ready to build a more industry-ready campus?
 *
 * VERIFIED AGAINST skillgenpro-frontend (29/30-Sep-2026):
 *  · College portal (CollegeHomeSideBar): Dashboard, College Profile,
 *    Students (management + CSV bulk upload that emails credentials), Certificates
 *    (college logo, signing authority, Course/Bootcamp/Workshop, revoke),
 *    Analytics (CollegeAnalyticsDashboard: per-student activity across
 *    Bootcamp, Launchpad, Mock Interview and Tech Explorer, score, last
 *    active, status), GitHub (CollegeGitHubDashboard: repositories, commits,
 *    last commit, active/inactive), College Challenge, Purchase Credits.
 *  · Student portal on a college tenant (TenantSidebar): Elite Technology
 *    Mastery, AI Tutor, Tech Explorer, Launchpad, Technology MCQs, Bootcamps,
 *    Compiler, Mock Interview, notes, certifications.
 *  · Mock Interview (components/MockInterview): role selection; coding,
 *    technical, HR & behavioural rooms; aptitude / time-management test;
 *    result = overall assessment, competency breakdown, question-by-question
 *    review; delivery coaching computed in the browser.
 *  · Git: students add their GitHub handle and are invited to the official
 *    project repository (TenantGitHubProfile, NextGenEnrollment). Project
 *    repositories may be PRIVATE (the screenshot is) — never claim every
 *    repository is public, never invent commit counts.
 *  · ExiTracker: the founder's product overview
 *    (public/student-intro/exitracker-overview.*) — work-item hierarchy
 *    Backlog → Epic → Feature → User Story → Task → Sub Task → … → Release,
 *    capacity, velocity, workload, sprint/release burndown, project health.
 *    Its numbers are illustrative SAMPLE DATA: always labelled as such.
 *  · ExiHire: an AI-powered HIRING platform (founder, 29-Sep-2026) — jobs,
 *    applications, AI interview rounds. Never practice; practice is Mock
 *    Interview. College Challenge (CollegeChallenge.jsx): nominate students →
 *    registered on ExiHire → AI interviews → AI evaluation → results and
 *    bootcamp scholarships.
 *  · White-label (TenantContext): own subdomain, college name, logo, contact
 *    details. Custom brand colours are NOT offered (founder, 29-Sep-2026).
 *  · There is NO faculty dashboard and NO placement dashboard. Faculty and
 *    T&P value is described as what the college portal gives the staff who
 *    run the programme.
 *
 * CLAIM SAFETY — load-bearing, do not "strengthen":
 *  · Never a guaranteed job, placement, interview, salary, hiring or
 *    industry-readiness outcome. Use helps / supports / enables.
 *  · No student counts, college names, logos, placement rates or
 *    testimonials — none are verified.
 *  · TSG complements the curriculum; it never replaces it.
 */

/* ── Routes (existing only) ─────────────────────────────────────── */
export const REGISTER_PATH = '/collegesignup';
export const DEMO_PATH = '/book-demo';
export const NEXUS_ANCHOR = 'ci-needs';
// Official URL of this page. /college-intro only redirects here
// (CollegeIntroRedirect.jsx in-app; infra/cloudfront/tsg-product-engg-redirect.js at the edge).
export const PAGE_PATH = '/tsg-collab';
export const LOGO_SRC = '/college-intro/tsg-logo.png';

/* Sister products — the founder's own sites, opened in a new tab. */
export const URLS = {
  exihire: 'https://exihire.com/',
  exitracker: 'https://www.exitracker.com/',
};

/* ── CTA vocabulary — three labels, page-wide, no synonyms ──────── */
export const CTA = {
  primary: 'Partner With TSG',
  explore: 'Explore TSG Nexus',
  demo: 'Request a Demo',
};

/* ── Product images ────────────────────────────────────────────────
 * The SAME real assets the student page uses (one download, shared cache).
 * The college page shows them through `crops` — different regions, zoom
 * and callouts — so the same product tells an institutional story.
 * A crop is {x, y, w, h} in the image's own pixels; callouts are % of the
 * crop. `sources` are WebP widths for srcset; `fallback` is the original.
 * `sizes` is ONE value per image, used by every crop of it, so the browser
 * picks a single srcset candidate and all crops share one download. */
export const IMAGES = {
  github: {
    sources: [['/student-intro/github-repo-800.webp', 800], ['/student-intro/github-repo.webp', 1266]],
    fallback: '/student-intro/github-repo.jpg',
    w: 1266,
    h: 653,
    full: '/student-intro/github-repo.jpg',
    sizes: '(max-width: 960px) 800px, 1266px',
  },
  tracker: {
    sources: [['/student-intro/exitracker-overview-960.webp', 960], ['/student-intro/exitracker-overview.webp', 1536]],
    fallback: '/student-intro/exitracker-overview.jpg',
    w: 1536,
    h: 1024,
    full: '/student-intro/exitracker-overview.jpg',
    sizes: '(max-width: 960px) 960px, 1536px',
  },
  hire: {
    sources: [['/student-intro/exihire-report.webp', 900]],
    fallback: '/college-intro/exihire-report.jpg',
    w: 900,
    h: 1341,
    sizes: '900px',
  },
  portal: {
    sources: [['/college-intro/college-portal.webp', 1279]],
    fallback: '/college-intro/college-portal.png',
    w: 1279,
    h: 650,
    sizes: '1279px',
  },
};

/* ── 1 — Hero ───────────────────────────────────────────────────── */
export const HERO = {
  eyebrow: 'TSG Nexus for colleges',
  h1: { lead: 'Turn your campus into', hot: 'an engineering experience.' },
  lead:
    'Give students more than technology training. Create an environment where they learn, build, experience engineering workflows, practise interviews — and develop the confidence to step into the industry.',
  assurance: ['Complements your curriculum', 'Your college’s own portal', 'Visibility into every student'],
  visual: {
    college: { name: 'Your college', sub: 'yourcollege.theskillgenie.com' },
    core: { top: 'TSG', bottom: 'NEXUS' },
    students: 'Your students',
    nodes: [
      { id: 'learn', name: 'Learn', sub: 'Tracks + AI Tutor' },
      { id: 'build', name: 'Build', sub: 'Projects + Git' },
      { id: 'work', name: 'Engineer', sub: 'ExiTracker' },
      { id: 'assess', name: 'Assess', sub: 'Skill tests' },
      { id: 'practice', name: 'Practise', sub: 'Mock Interview' },
    ],
    outcome: 'Industry readiness',
    hiring: { name: 'Find a job', sub: 'ExiHire' },
    feedback: 'Progress visible to your college',
    // State labels only — real product vocabulary, never numbers.
    fragments: [
      { id: 'issue', kicker: 'ExiTracker · User Story', from: 'In Progress', to: 'Code Review' },
      { id: 'report', kicker: 'Mock Interview', line: 'Competency breakdown ready' },
      { id: 'repo', kicker: 'College portal · GitHub', line: 'Repository activity visible' },
    ],
  },
  micro: { a: 'From classroom learning', b: 'to real engineering experience.' },
  // The six verbs, read from the INSTITUTION's side.
  rail: [
    { id: 'learn', verb: 'Learn', where: 'Structured student development' },
    { id: 'build', verb: 'Build', where: 'Practical project experience' },
    { id: 'experience', verb: 'Experience', where: 'Engineering workflows' },
    { id: 'assess', verb: 'Assess', where: 'Measure student progress' },
    { id: 'practice', verb: 'Practise', where: 'Interview readiness' },
    { id: 'demonstrate', verb: 'Demonstrate', where: 'Evidence of skills' },
  ],
};

/* ── 2 — Why colleges need more than training ───────────────────── */
export const CHALLENGE = {
  eyebrow: 'The institutional challenge',
  title: ['Why colleges need', 'more than training.'],
  lead:
    'Students complete courses, collect certificates and attend training — and all of that matters. But the industry also asks them to apply what they know, work the way teams work, and show it.',
  gapLabel: 'Where students are often asked to go further',
  gaps: [
    'Apply knowledge to a real project',
    'Work with source code and version control',
    'Understand how engineering work is organised',
    'Explain technical decisions in an interview',
  ],
  equation: {
    a: { name: 'Academics', body: 'Concepts, fundamentals and examinations — taught by your faculty.' },
    b: { name: 'TSG Nexus', body: 'Projects, workflows, assessments and interview practice.' },
    result: { name: 'Practical industry experience', body: 'Students who understand the theory and have used it.' },
  },
  note: 'TSG Nexus complements your curriculum and your faculty. Nothing in your syllabus is replaced.',
};

/* ── 3 — What colleges need → what TSG Nexus enables ───────────────
 * `evidence` names an IMAGES crop shown in the proof panel, or null. */
export const NEEDS = {
  eyebrow: 'Need → capability',
  title: ['What colleges need.', 'What TSG Nexus enables.'],
  lead:
    'Industry asks for more than technical knowledge. TSG Nexus gives your students practical exposure to selected workflows used in modern engineering teams — it does not claim to replicate an entire workplace.',
  industryLabel: 'What industry increasingly expects',
  industry: ['Technical knowledge', 'Practical projects', 'Version control', 'Engineering workflows', 'Collaboration', 'Problem solving', 'Communication', 'Interview readiness'],
  rows: [
    { id: 'apply', need: 'Students who can apply what they learn', enables: 'Project-based practical experience', where: 'TSG Nexus', proof: 'Students enrol in a project with an engineering role, get AI code review on what they write, and follow project versions and sprints.', evidence: null, anchor: 'ci-students-title' },
    { id: 'git', need: 'Students who can demonstrate their technical work', enables: 'Git and repository experience', where: 'GitHub', proof: 'Students are invited to a project repository and commit, branch and open pull requests. Your college sees repositories and commit activity.', evidence: 'git', anchor: 'ci-git-title' },
    { id: 'workflow', need: 'Students who understand modern engineering workflows', enables: 'ExiTracker work management', where: 'ExiTracker', proof: 'Epics, user stories, tasks and releases — planned on boards, tracked through sprints, delivered.', evidence: 'tracker', anchor: 'ci-tracker-title' },
    { id: 'interview', need: 'Students who practise before interviews', enables: 'Mock Interview', where: 'TSG Nexus', proof: 'Coding, technical, HR & behavioural, aptitude and time-management rounds, each ending in a report.', evidence: null, anchor: 'ci-mock-title' },
    { id: 'hiring', need: 'A path from experience to a job', enables: 'ExiHire — AI-powered hiring platform', where: 'Evolve X ecosystem', proof: 'Once students have built projects and engineering experience, they can find a job on ExiHire itself: discover openings, apply, and go through AI interview rounds with employers.', evidence: 'jobs', anchor: 'ci-hire-title' },
    { id: 'visibility', need: 'Visibility into participation and progress', enables: 'The college portal', where: 'Your portal', proof: 'Per-student activity, scores and last-active dates; a GitHub dashboard; certificates issued in your name.', evidence: 'portal', anchor: 'ci-people-title' },
  ],
};

/* ── 3b — The difference (institutional comparison) ─────────────────
 * Structural, never competitive: no platform, college or provider is named,
 * and nothing claims that others lack a capability. The left column describes
 * a common, disconnected arrangement ("may", "often"); the right column is
 * ONLY shipped capability, verified 30-Sep-2026:
 *   · Elite Technology Mastery; TSG AI Tutor (explain / notes / skill test /
 *     code review / resume review / interview) — /nextgen-builder/ai-tutor/*
 *   · Multilingual: AI Tutor + Elite pages share one language list
 *     (components/aiTutor/languages.js) — English plus Hindi, Telugu, Tamil,
 *     Kannada, Malayalam, Marathi, Gujarati, Bengali, Punjabi, Urdu, Odia,
 *     Assamese and several international languages. Elite translation is
 *     plan-limited (free trial = 5 pages per technology), hence "support".
 *   · Resume: an AI resume REVIEW exists; there is no resume builder — never
 *     write "Resume Builder" here.
 *   · Bootcamps: colleges enrol students (TenantBootcampEnrollment); no
 *     duration is stated because none is fixed in configuration.
 *   · Student dashboard: /nextgen-builder/dashboard (project versions and
 *     progress, AI code-review score, feature allowances, referrals) and the
 *     college-tenant student dashboard (topics completed, hours learned, AI
 *     tutor sessions, compiler runs, mock interviews, streaks). There is NO
 *     approved dashboard screenshot — the tenant dashboard still carries
 *     placeholder values — so the dashboard is described, never pictured.
 *   · Certificates: certificateRoutes/dualLogoCertificateGenerator.js — a
 *     three-brand header (college logo + name | TheSkillGenie | EvolveX),
 *     institution-authority signature, a proof-of-work strip (grade, score,
 *     tasks, commits, PRs) and a QR / URL for verification. The college logo
 *     is required when a college issues one. The preview image is that
 *     generator's real output rendered with PLACEHOLDER details (no college
 *     logo, "Student Name"), cropped above the proof-of-work numbers.
 *     No accreditation, recognition or employer acceptance is claimed.
 *  · PRICE: ₹9,999 is a per-student figure, so it never appears here. */
export const DIFFERENCE = {
  eyebrow: 'The difference',
  title: ['From academic learning', 'to industry experience.'],
  lead:
    'Colleges already teach technology. TSG Nexus connects that learning with building, engineering workflows, project experience, interview preparation and a path to jobs on ExiHire.',
  heads: {
    old: { kicker: 'A traditional, disconnected approach', title: 'Separate activities, separate places' },
    nexus: { kicker: 'With TSG Nexus', title: 'One connected student experience' },
  },
  groups: [
    {
      id: 'learn',
      label: 'Learn',
      rows: [
        { id: 'learning', icon: 'book', dim: 'Learning', old: 'Academic learning and additional training often happen as separate activities.', nexus: 'Elite Technology Mastery tracks — structured learning, joined to practical building.', tags: ['Elite content'] },
        { id: 'ai', icon: 'bot', dim: 'AI learning support', old: 'Students depend on separate resources for extra learning and coding help.', nexus: 'The TSG AI Tutor explains concepts, keeps notes and runs skill tests — with support for Hindi, Telugu, Tamil and other Indian languages.', tags: ['AI Tutor', 'Multilingual'] },
        { id: 'programmes', icon: 'layers', dim: 'Structured programmes', old: 'Training is delivered as separate, one-off programmes.', nexus: 'Bootcamps your college enrols students into, on the same platform as everything else.', tags: ['Bootcamps'] },
      ],
    },
    {
      id: 'build',
      label: 'Build',
      rows: [
        { id: 'projects', icon: 'hammer', dim: 'Project building', old: 'Students find or invent projects on their own.', nexus: 'TSG Nexus projects: students enrol with an engineering role and work through versions and sprints.', tags: ['Projects'] },
        { id: 'review', icon: 'scan', dim: 'Code review', old: 'Feedback on code needs separate tools, mentors or manual effort.', nexus: 'AI code review gives structured feedback on the code a student actually wrote.', tags: ['AI code review'] },
        { id: 'workflow', icon: 'kanban', dim: 'Engineering workflow', old: 'Limited exposure to how software projects are structured and delivered.', nexus: 'ExiTracker: epics, stories, tasks, boards and sprints — workflows inspired by modern engineering teams.', tags: ['ExiTracker'] },
        { id: 'git', icon: 'git', dim: 'Proof of work', old: 'Project work rarely has a consistent, professional home.', nexus: 'Git-based work in a project repository — commits, branches and pull requests students can show.', tags: ['GitHub'] },
      ],
    },
    {
      id: 'practise',
      label: 'Practise',
      rows: [
        { id: 'resume', icon: 'file', dim: 'Resume preparation', old: 'Resumes are prepared with separate tools, disconnected from the work.', nexus: 'AI resume review — feedback on the profile, backed by the projects the student built.', tags: ['Resume review'] },
        { id: 'mock', icon: 'mic', dim: 'Interview practice', old: 'Interview preparation arrives as a separate programme, often late.', nexus: 'Mock Interview: coding, technical, HR and aptitude rounds, each with a feedback report.', tags: ['Practice + feedback'] },
      ],
    },
    {
      id: 'connect',
      label: 'Connect',
      rows: [
        { id: 'dashboard', icon: 'dashboard', dim: 'Student dashboard', old: 'Student activity is spread across different tools and systems.', nexus: 'A dedicated student dashboard: projects and progress, AI code-review score, learning activity and mock-interview practice in one place.', tags: ['Student dashboard'], feature: true },
        { id: 'certificate', icon: 'award', dim: 'Certificates', old: 'Certificates come from individual providers or programmes.', nexus: 'Co-branded certificates carrying your college’s name and logo alongside The Skill Genie — verifiable by QR.', tags: ['Co-branded'], feature: true },
        { id: 'hire', icon: 'briefcase', dim: 'Finding a job', old: 'Students build skills in one place, then search for jobs somewhere else.', nexus: 'After building experience, students find jobs, apply and interview on ExiHire — in the same ecosystem.', tags: ['ExiHire'] },
      ],
    },
  ],
  oldResult: { label: 'The result', text: 'Many separate places — for the student and for the college.' },
  nexusResult: { label: 'The result', text: 'One connected student experience.' },
  showMore: 'Show all comparisons',
  showLess: 'Show fewer',
  footnote: 'Feature access follows your partnership plan. Mock Interview is practice; ExiHire is hiring — they are different products.',

  story: {
    eyebrow: 'From student activity to institutional recognition',
    title: ['Your college stays part', 'of every achievement.'],
    steps: [
      { id: 'dashboard', name: 'Student dashboard', body: 'Learning, projects, progress and practice — in one place.' },
      { id: 'achieve', name: 'Student achievement', body: 'Completed programmes, with the work behind them.' },
      { id: 'certificate', name: 'Co-branded certificate', body: 'Issued from your college portal, under your name.' },
    ],
    carries: { label: 'The student carries both', a: 'Your college', b: 'TSG Nexus' },
    certificate: {
      sources: [['/college-intro/certificate-cobranded-v3-900.webp', 900], ['/college-intro/certificate-cobranded-v3.webp', 1600]],
      fallback: '/college-intro/certificate-cobranded-v3.jpg',
      w: 1600,
      h: 535,
      sizes: '(max-width: 960px) 900px, 1600px',
      alt: 'A co-branded TSG certificate of completion: the college’s logo and name on the left (shown for EXI Test Engineering College), The Skill Genie in the centre and Evolve X Innovations on the right, above the student’s name and programme. Shown with a sample college and placeholder student details.',
      label: 'Real certificate design · sample college',
    },
    features: [
      'Your college’s logo and name',
      'Signed by your institution authority',
      'Can carry grade, tasks, commits and pull requests',
      'Verifiable by QR code and link',
    ],
    note: 'A certificate of programme completion — not a degree or an academic qualification.',
  },

  gain: {
    eyebrow: 'The institutional value',
    title: 'What does the college actually gain?',
    pillars: [
      { n: '01', title: 'Student development', body: 'Students move beyond passive learning into structured learning and building.' },
      { n: '02', title: 'Practical engineering experience', body: 'Project workflows and development practices, alongside the curriculum.' },
      { n: '03', title: 'Student visibility', body: 'Learning and project activity visible in your college portal.' },
      { n: '04', title: 'Proof of work', body: 'Repositories, commits and projects — tangible evidence of what students can do.' },
      { n: '05', title: 'Interview readiness', body: 'Practice and feedback before students face a real interview.' },
      { n: '06', title: 'A path to jobs', body: 'Students move from project experience to finding and applying for jobs on ExiHire.' },
      { n: '07', title: 'Institutional recognition', body: 'Co-branded certificates connect student achievement with your college.' },
      { n: '08', title: 'Beyond the classroom', body: 'A structured digital engineering experience students can reach wherever they are.' },
    ],
  },

  close: {
    lines: ['Your students don’t need just another course.', 'They need an environment where they can learn, build, experience engineering and show what they can do.'],
    punch: 'TSG Nexus connects that journey.',
    cta: 'Explore TSG Nexus for Your Institution',
  },
};

/* ── 4 — The central loop ───────────────────────────────────────── */
export const JOURNEY = {
  eyebrow: 'One student journey',
  title: ['From your campus', 'to a job on ExiHire.'],
  lead: 'Who does what, and where it happens — the path your college makes possible.',
  steps: [
    { id: 'college', who: 'College', title: 'Enables', body: 'Onboards students in bulk; each receives their own login.', where: 'College portal', icon: 'college' },
    { id: 'nexus', who: 'TSG Nexus', title: 'Provides the ecosystem', body: 'Tracks, projects, workflows, assessments and interview practice on one platform.', where: 'TSG Nexus', icon: 'nexus' },
    { id: 'student', who: 'Student', title: 'Learns, builds, practises', body: 'Structured tracks with an AI tutor, coding practice and skill tests.', where: 'TSG Nexus', icon: 'learn' },
    { id: 'project', who: 'Student', title: 'Gains project experience', body: 'Works in a real repository and moves work through ExiTracker.', where: 'Git + ExiTracker', icon: 'build' },
    { id: 'interview', who: 'Student', title: 'Becomes interview-ready', body: 'Sits mock rounds and works on what the report names.', where: 'Mock Interview', icon: 'mic' },
    { id: 'hiring', who: 'Student', title: 'Finds a job', body: 'Discovers openings on ExiHire, applies, and interviews with employers through AI rounds.', where: 'ExiHire', icon: 'hire' },
  ],
  outcome: { title: 'Industry-oriented experience', body: 'Knowledge, plus the experience of using it — and evidence to show for it.' },
};

/* ── 5 — Your students get more than course content ─────────────── */
export const STUDENTS = {
  eyebrow: 'Student advantage',
  title: ['Your students get more', 'than course content.'],
  lead: 'Your institution can give students a connected journey from learning to practical experience — every feature below lives in the student portal your college provides.',
  groups: [
    {
      id: 'learn', verb: 'Learn',
      college: 'Structured development between and beyond classes.',
      items: [
        { name: 'Elite Technology Mastery', body: 'Structured tracks through a technology, fundamentals first.' },
        { name: 'AI Tutor', body: 'On-demand support while students work through technical concepts.' },
        { name: 'Tech Explorer & Launchpad', body: 'Technology documents, guided levels and practice.' },
      ],
    },
    {
      id: 'build', verb: 'Build',
      college: 'Practical work you can see, not only attendance.',
      items: [
        { name: 'Project enrolment', body: 'A real project, a repository and pull requests.' },
        { name: 'AI code review', body: 'Specific feedback on the code a student wrote.' },
        { name: 'Bootcamps', body: 'Structured programmes your college enrols students into.' },
      ],
    },
    {
      id: 'assess', verb: 'Assess',
      college: 'Measured skill, not self-reported skill.',
      items: [
        { name: 'Skill tests', body: 'Scored checks that show strengths and gaps.' },
        { name: 'Technology MCQs', body: 'Topic-level practice across tracks.' },
        { name: 'Online compiler', body: 'Write and run code with no local setup — useful for every lab.' },
      ],
    },
    {
      id: 'practice', verb: 'Practise',
      college: 'Preparation that starts before drive season.',
      items: [
        { name: 'Mock Interview', body: 'Coding, technical and HR & behavioural rounds.' },
        { name: 'Resume review', body: 'AI feedback on the page recruiters read first.' },
        { name: 'Aptitude & time management', body: 'Timed practice for the rounds before the interview.' },
      ],
    },
  ],
  footnote: 'Feature access follows your partnership plan.',
};

/* ── 6 — Git: build evidence ─────────────────────────────────────── */
export const GIT = {
  eyebrow: 'Git & GitHub · Build evidence',
  title: ['Help students build evidence', 'of what they can do.'],
  lead:
    'Students add their GitHub handle and are invited to their project’s repository. They learn to work with source code the way engineering teams do — and their contributions sit in their own account.',
  // Crop: the repository header, branches, commit history and README —
  // the About sidebar is left out. Callouts are % of the crop.
  crop: { x: 0, y: 0, w: 930, h: 560 },
  callouts: [
    { id: 'repo', label: 'A project repository named for the college bootcamp', x: 64, y: 23.5 },
    { id: 'history', label: 'Branches and commit history', x: 15.5, y: 31 },
    { id: 'readme', label: 'Documented, managed project setup', x: 17.5, y: 74.5 },
  ],
  url: 'github.com/evolvexinnovations/…-test-engineering-college',
  alt: 'A real TSG Nexus bootcamp repository on GitHub named for a test engineering college: private repository, main branch, 2 branches, commit history, .gitignore and README files, and a README titled “Test Engineering College — SkillGenie Bootcamp Repository”, managed by SkillGenie.',
  label: 'Real repository · cropped',
  values: [
    { id: 'college', kicker: 'College value', title: 'Build evidence of student work.', body: 'Encourage a culture where students build visible evidence of practical work, not only certificates.' },
    { id: 'industry', kicker: 'Industry connection', title: 'Modern teams work in repositories.', body: 'Commits, branches and pull requests are how engineering work is shared and reviewed.' },
    { id: 'visibility', kicker: 'In your portal', title: 'See the activity.', body: 'The GitHub dashboard lists student repositories, commits, last commit and active status.' },
  ],
  note: 'Project repositories can be private to the team, as this one is. We never publish commit counts on students’ behalf.',
};

/* ── 7 — ExiTracker ─────────────────────────────────────────────── */
export const TRACKER = {
  eyebrow: 'ExiTracker · Structured engineering experience',
  title: ['Turn academic projects into', 'structured engineering experience.'],
  lead:
    'Students experience workflows inspired by modern engineering teams, and your college gains a more organised framework around practical project development.',
  // Four crops of the same overview, each a different institutional
  // question: how work is structured, what students and teams are doing,
  // how the project is progressing, and how visible delivery is.
  structure: {
    crop: { x: 16, y: 84, w: 1504, h: 126 },
    label: 'Project structure',
    alt: 'The ExiTracker work-item hierarchy: Backlog, Epic, Feature, User Story, Task, Sub Task, Bug, Improvement, Spike, Dependency, Milestone and Release.',
  },
  team: {
    crop: { x: 16, y: 224, w: 504, h: 362 },
    label: 'Student work & team workflow',
    alt: 'ExiTracker team and execution metrics: team capacity, planned versus actual, velocity, commitment reliability, workload distribution, utilisation, work in progress, blocked work and focus time.',
    callouts: [
      { id: 'team', label: 'Team workflow: capacity & velocity', x: 29, y: 19.5 },
      { id: 'work', label: 'Student work: workload, work in progress & blocked items', x: 66, y: 42 },
    ],
  },
  progress: {
    crop: { x: 1012, y: 224, w: 508, h: 366 },
    label: 'Progress',
    alt: 'ExiTracker flow and delivery analytics: sprint burndown, release burndown, burnup chart, cumulative flow, cycle and lead time, throughput and bottleneck detection.',
    callouts: [
      { id: 'progress', label: 'Progress: sprint & release burndown', x: 25, y: 17 },
    ],
  },
  health: {
    crop: { x: 16, y: 748, w: 1504, h: 128 },
    label: 'Delivery visibility',
    alt: 'ExiTracker executive view: project health, release forecast, roadmap progress, milestone confidence, budget burn, resource forecast, risk heatmap and schedule variance.',
  },
  sampleLabel: 'ExiTracker product overview · sample data',
  flow: ['Academic project', 'Structure', 'Tasks', 'Progress', 'Collaboration', 'Completion'],
  flowCaption: 'Give students an experience closer to how structured engineering work is organised.',
  values: [
    { id: 'structure', title: 'Project structure', body: 'Break a semester project into epics, stories, tasks and releases.' },
    { id: 'work', title: 'Student work', body: 'Assignment, priority and status for every piece of work.' },
    { id: 'team', title: 'Team workflow', body: 'Boards, sprints, dependencies, meetings and a knowledge hub.' },
    { id: 'delivery', title: 'Delivery visibility', body: 'Burndown and progress that show where a project really stands.' },
  ],
};

/* ── 8 — Mock Interview ─────────────────────────────────────────── */
export const MOCK = {
  eyebrow: 'Mock Interview · Practice',
  title: ['Help students practise', 'before the real interview.'],
  lead:
    'A structured opportunity to practise technical and behavioural interviews and understand what to improve — for every student, not only those selected for a drive.',
  rounds: ['Coding', 'Technical', 'HR & Behavioural', 'Aptitude', 'Time management'],
  report: {
    label: 'What every round returns',
    parts: [
      { id: 'overall', name: 'Overall assessment', body: 'A clear read on how the round went.' },
      { id: 'competency', name: 'Competency breakdown', body: 'Strengths and weak areas, scored separately.' },
      { id: 'qa', name: 'Question-by-question review', body: 'Each answer, with feedback on it.' },
      { id: 'delivery', name: 'Delivery coaching', body: 'Eye contact, speaking pace, pauses and filler words.' },
    ],
  },
  benefits: [
    { id: 'ready', title: 'Interview readiness', body: 'Students rehearse the rounds they will face.' },
    { id: 'gap', title: 'Skill-gap awareness', body: 'Reports name weak areas, not just a score.' },
    { id: 'visible', title: 'Visible to your college', body: 'Mock Interview activity appears in college analytics.' },
  ],
  privacy: 'Delivery coaching is computed in the student’s browser. Video and audio never leave the device.',
};

/* ── 9 — ExiHire ────────────────────────────────────────────────── */
export const HIRE = {
  eyebrow: 'ExiHire · AI-powered hiring platform',
  title: ['Your students build the experience.', 'Then find the job on ExiHire.'],
  lead:
    'ExiHire is the AI-powered hiring platform of the Evolve X ecosystem. Once your students have built projects and engineering experience with TSG Nexus, they can discover jobs, apply and interview with employers on ExiHire itself — where they are assessed on what they can actually do.',
  chain: [
    { id: 'nexus', name: 'TSG Nexus', role: 'Learn + build' },
    { id: 'experience', name: 'Experience', role: 'Projects · Git · ExiTracker' },
    { id: 'hire', name: 'ExiHire', role: 'Find a job' },
  ],
  // How a job search moves on ExiHire, in the words the student page uses.
  // No practice steps here — Mock Interview is practice, ExiHire is hiring.
  jobPath: [
    { id: 'discover', name: 'Discover jobs', body: 'Browse openings from employers.' },
    { id: 'apply', name: 'Apply', body: 'Apply from the same platform.' },
    { id: 'interview', name: 'AI interview rounds', body: 'AI Coding, Technical and HR rounds.' },
    { id: 'decision', name: 'Employer decision', body: 'Evaluation and next-round recommendation.' },
  ],
  // Three regions of the same real report: how employers assess an applicant
  // at the AI-interview step. y/h are the report's own pixels.
  crops: [
    { id: 'rounds', label: 'AI interview', note: 'Separate AI Coding and AI HR rounds, each scored out of 100.', crop: { x: 10, y: 244, w: 690, h: 136 } },
    { id: 'evaluation', label: 'Evaluation', note: 'Skills scored separately — integrity and proctoring included.', crop: { x: 20, y: 760, w: 860, h: 210 } },
    { id: 'workflow', label: 'Hiring decision', note: 'A clear recommendation on the next round, for the employer.', crop: { x: 20, y: 1200, w: 860, h: 120 } },
  ],
  stackTitle: 'How employers assess an applicant on ExiHire',
  alt: 'Regions of a real ExiHire applicant report from a job interview, with the applicant’s name redacted: AI Coding and AI HR round scores out of 100; a score breakdown across communication, SQL, problem solving and integrity/proctoring; and a final recommendation to proceed to the next round.',
  label: 'Real applicant report · name redacted',
  flow: ['Learn with TSG Nexus', 'Build projects', 'Gain engineering experience', 'Find jobs on ExiHire'],
  flowHot: 'Find jobs on ExiHire',
  flowLead: 'Training doesn’t have to end at a certificate. It can end at a job application.',
  challenge: {
    title: 'The College Challenge — ExiHire from your portal',
    body: 'Your institution can nominate students, have them registered on ExiHire for AI interview rounds, then see the AI evaluation and an overall college performance view — with scholarship benefits on TSG bootcamps for qualifying students.',
    steps: ['Nominate students', 'AI interviews', 'AI evaluation', 'Results & scholarships'],
  },
  boundary: 'ExiHire gives your students a place to find jobs and be assessed on their skills. Hiring decisions stay with employers — no platform can promise a job, and we don’t.',
};

/* ── 10 — Faculty · T&P · Management ────────────────────────────── */
export const PEOPLE = {
  eyebrow: 'For the people who run the programme',
  title: ['Built for students.', 'Valuable for your institution.'],
  lead: 'Faculty, the Training & Placement cell and management all work from the same college portal — there is no separate tool to learn.',
  faculty: {
    kicker: 'Faculty',
    title: 'Valuable for faculty.',
    items: [
      'See which students are active, and which have gone quiet.',
      'Open a student’s repository and commit history before a project review.',
      'Check skill-test and mock-interview scores before mentoring.',
      'Enrol students into structured bootcamps and issue certificates.',
    ],
  },
  placement: {
    kicker: 'Training & Placement',
    title: 'A more connected student journey.',
    flow: ['Student development', 'Project experience', 'Assessment', 'Mock Interview', 'Jobs on ExiHire'],
    body: 'The cell can support a structured preparation journey across the year — and nominate students for AI interview rounds through the College Challenge.',
    opportunity: 'The three best-performing TSG Nexus students get the opportunity to interview with Evolve X Innovations. It is an interview opportunity, not a job offer — recruitment depends on performance.',
  },
  management: {
    kicker: 'Management',
    title: ['From student activity', 'to institutional visibility.'],
    body: 'Participation, activity, scores, repositories and certificates in one portal — and evidence of the capability-enhancement work NAAC and NIRF reporting asks institutions to show.',
    shot: {
      crop: { x: 283, y: 222, w: 940, h: 332 },
      alt: 'A real college admin portal: total students, credits available, certificates issued and platform status, with quick actions for student management, analytics on skill progress, activity and placement readiness, certificates, bulk upload, top performers and purchasing credits.',
      label: 'Real college portal',
    },
    flow: ['Student activity', 'Insights', 'Institutional visibility', 'Better programme management'],
  },
};

/* ── 11 — White-label ───────────────────────────────────────────── */
export const BRAND = {
  eyebrow: 'White-label',
  title: ['Your college. Your brand.', 'Your student experience.'],
  lead:
    'Eligible college partnerships run the platform on their own subdomain, under their own name — so students experience it as part of your institution.',
  equation: ['Your college brand', 'TSG technology', 'Your college’s experience'],
  items: [
    { id: 'domain', name: 'Your own subdomain', body: 'yourcollege.theskillgenie.com' },
    { id: 'identity', name: 'Your name and logo', body: 'Across the admin and student portals.' },
    { id: 'contact', name: 'Your contact details', body: 'Phone, email and website shown to students.' },
  ],
  offer: 'Free for eligible college partnerships.',
  portal: {
    url: 'yourcollege.theskillgenie.com',
    alt: 'The Skill Genie college admin portal running under a college’s own name, with its logo, student totals, credits, certificates and quick actions.',
  },
};

/* ── 15 — Getting started + terms ───────────────────────────────── */
export const START = {
  eyebrow: 'Getting started',
  title: 'Get started without complexity.',
  lead: 'Six steps, all inside the product your college will use.',
  steps: [
    { n: '01', title: 'Partner', body: 'Register your college and choose a partnership.' },
    { n: '02', title: 'Configure', body: 'Set up your college profile and logo.' },
    { n: '03', title: 'Onboard students', body: 'Upload students in bulk; credentials go out by email.' },
    { n: '04', title: 'Launch programmes', body: 'Enrol students in bootcamps, tracks and challenges.' },
    { n: '05', title: 'Track progress', body: 'Follow activity, scores and repositories in analytics.' },
    { n: '06', title: 'Improve', body: 'Act on what you see; certify what students complete.' },
  ],
};

export const PRICING = {
  referenceLabel: 'Reference value',
  reference: '₹19,999',
  label: 'College partnership',
  amount: '₹9,999',
  unit: 'per student',
};

export const TERMS = {
  title: 'Partnership terms',
  signals: [
    { id: 'white-label', label: 'White-label', value: 'Free for eligible college partnerships' },
    { id: 'ecosystem', label: 'Exi ecosystem', value: 'ExiTracker and ExiHire access included' },
    {
      id: 'hundred',
      // A contribution BACK to the institution, not a discount on the fee.
      // No amount is stated because the application configures none.
      label: '100+ students',
      value: 'A commitment contribution to your institution',
      note: 'When a college brings 100+ students into the programme, we put a sum back into the institution’s own student-development work — a token of commitment, not a discount on the fee. Ask us for the current figure.',
      accent: true,
    },
  ],
  includes: [
    'TSG Nexus learning ecosystem',
    'Project work with GitHub',
    'ExiTracker access',
    'Mock Interview practice',
    'ExiHire access',
    'College analytics and GitHub dashboards',
  ],
};

/* ── 16 — Close ─────────────────────────────────────────────────── */
export const CLOSE = {
  title: ['Ready to build a more', 'industry-ready campus?'],
  body: 'Give your students an environment where learning can turn into practical experience.',
  statement: 'We don’t promise students a job. We help them build the evidence that they can do the job.',
};

/* ── SEO ────────────────────────────────────────────────────────── */
export const SEO = {
  title: 'TSG Nexus for Colleges — Industry Readiness & Engineering Experience | The Skill Genie',
  description:
    'TSG Nexus for colleges: engineering experience around academics — student projects, engineering workflows, interview practice, dashboards and co-branded certificates.',
  keywords:
    'TSG Nexus for colleges, college industry readiness, student skill development, engineering experience, project-based learning, technology ecosystem for colleges, interview readiness, AI hiring',
  canonical: 'https://www.theskillgenie.com/tsg-collab',
  ogTitle: 'TSG Nexus for Colleges — Turn your campus into an engineering experience',
  ogDescription:
    'What your institution can enable: learning, real projects, engineering workflows, interview practice and a path to jobs on ExiHire — with visibility for faculty, T&P and management.',
  ogImage: 'https://skillgenie-college-uploads.s3.ap-south-1.amazonaws.com/TSG_Logo.png',
};
